import { type RefObject, useEffect, useRef } from 'react';

import { calculateParallaxBounds, calculateParallaxTranslate } from '../utils/calculateParallaxTranslate';
import { BACKGROUND_MAX_HEIGHT } from '../constants';

export type TUseParallaxResult = {
  containerRef: RefObject<HTMLDivElement | null>;
  animatedBgRef: RefObject<HTMLDivElement | null>;
};

/**
 * Эффект параллакса для фонового слоя баннера: при скролле смещает фон
 * относительно контейнера.
 *
 * Позиция считается через `getBoundingClientRect() + window.scrollY`, а не через
 * `offsetTop`, чтобы корректно работать, когда баннер находится далеко от верха
 * страницы (например, в ленте объявлений) и лежит внутри абсолютно
 * спозиционированной обёртки adfox.
 *
 * Геометрия пересчитывается через `ResizeObserver` на контейнере.
 */
export function useParallax(): TUseParallaxResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const animatedBgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    let topPosition = 0;
    let bottomPosition = 0;
    let containerHeight = 0;
    let rafId: number | null = null;

    const getParams = (): void => {
      const absoluteTop = container.getBoundingClientRect().top + window.scrollY;

      containerHeight = container.offsetHeight;

      const bounds = calculateParallaxBounds(absoluteTop, containerHeight, window.innerHeight);

      topPosition = bounds.topPosition;
      bottomPosition = bounds.bottomPosition;
    };

    const updateParallax = (): void => {
      rafId = null;

      const background = animatedBgRef.current;

      if (!background) {
        return;
      }

      const translate = calculateParallaxTranslate({
        backgroundMaxHeight: BACKGROUND_MAX_HEIGHT,
        bottomPosition,
        containerHeight,
        scrollY: window.scrollY,
        topPosition,
        viewportHeight: window.innerHeight,
      });

      background.style.transform = translate == null ? '' : `translateY(${translate}px)`;
    };

    // Троттлим обновление до одного кадра: пока запрошенный rAF не отработал,
    // повторные скроллы не плодят лишних вызовов.
    const scheduleUpdate = (): void => {
      if (rafId === null) {
        rafId = requestAnimationFrame(updateParallax);
      }
    };

    const handleLayoutChange = (): void => {
      getParams();
      scheduleUpdate();
    };

    getParams();
    scheduleUpdate();

    const resizeObserver = new ResizeObserver(handleLayoutChange);

    resizeObserver.observe(container);

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', handleLayoutChange);

    return (): void => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', handleLayoutChange);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return { animatedBgRef, containerRef };
}
